/**
 * Client React Query des edge functions Alwaysdata (admin).
 *
 * Toutes les mutations passent par des edge fns (le token vit dans Vault côté
 * serveur, jamais côté client). Calque du pattern `blog-client.ts`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AlwaysdataConfig, AlwaysdataMailbox } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';

export class AlwaysdataError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'AlwaysdataError';
  }
}

async function invokeAlwaysdata<T>(fn: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>(fn, { body });
  if (error) {
    let message = error.message;
    let code: string | undefined;
    let details: unknown;
    const ctx = (error as Error & { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = (await ctx.json()) as {
          error?: string;
          message?: string;
          details?: unknown;
        };
        code = parsed.error;
        if (parsed.message) message = parsed.message;
        else if (parsed.error) message = parsed.error;
        details = parsed.details;
      } catch {
        /* garde le message générique */
      }
    }
    throw new AlwaysdataError(message, code, details);
  }
  if (!data) throw new AlwaysdataError('empty_response');
  return data;
}

// ─── Config ──────────────────────────────────────────────────────────────────

const CONFIG_KEY = ['admin-alwaysdata-config'] as const;

export function useAlwaysdataConfig() {
  return useQuery({
    queryKey: CONFIG_KEY,
    queryFn: async (): Promise<AlwaysdataConfig | null> => {
      // Lecture directe PostgREST (table admin-only via RLS).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const db = supabase as unknown as { from: (t: string) => any };
      const { data, error } = await db
        .from('alwaysdata_config')
        .select('provider, api_url, domain_name, allowed_ips, token_set, updated_at')
        .eq('provider', 'alwaysdata')
        .maybeSingle();
      if (error) throw new AlwaysdataError(error.message, error.code);
      return (data ?? null) as AlwaysdataConfig | null;
    },
  });
}

export interface UpdateAlwaysdataConfigInput {
  api_url?: string;
  domain_name?: string;
  allowed_ips?: string | null;
  /** Si fourni non-vide → stocké dans Vault côté edge fn. */
  token?: string;
}

export function useUpdateAlwaysdataConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateAlwaysdataConfigInput) => {
      const payload: Record<string, unknown> = {};
      if (input.api_url !== undefined) payload.api_url = input.api_url;
      if (input.domain_name !== undefined) payload.domain_name = input.domain_name;
      if (input.allowed_ips !== undefined) payload.allowed_ips = input.allowed_ips;
      if (input.token !== undefined && input.token.trim()) payload.token = input.token.trim();
      return invokeAlwaysdata<{ ok: boolean; config: AlwaysdataConfig }>(
        'alwaysdata-config-update',
        payload,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CONFIG_KEY }),
  });
}

// ─── Mailboxes (redirections) ────────────────────────────────────────────────

const MAILBOXES_KEY = ['admin-alwaysdata-mailboxes'] as const;

export interface MailboxesResult {
  domain: { id: number; name: string };
  mailboxes: AlwaysdataMailbox[];
}

export function useAlwaysdataMailboxes(enabled: boolean) {
  return useQuery({
    queryKey: MAILBOXES_KEY,
    enabled,
    queryFn: async (): Promise<MailboxesResult> => {
      const res = await invokeAlwaysdata<{
        ok: boolean;
        domain: { id: number; name: string };
        mailboxes: AlwaysdataMailbox[];
      }>('alwaysdata-mailboxes-list', {});
      return { domain: res.domain, mailboxes: res.mailboxes };
    },
    staleTime: 30_000,
  });
}

export interface CreateMailboxInput {
  name: string;
  redirect_to: string;
  redirect_enabled?: boolean;
  redirect_local_copy?: boolean;
  redirect_srs?: boolean;
  annotation?: string | null;
}

export function useCreateMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMailboxInput) =>
      invokeAlwaysdata<{ ok: boolean; mailbox: unknown }>('alwaysdata-mailbox-upsert', {
        ...input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MAILBOXES_KEY }),
  });
}

export interface UpdateMailboxInput {
  id: number;
  redirect_enabled?: boolean;
  redirect_to?: string;
  redirect_local_copy?: boolean;
  redirect_srs?: boolean;
  annotation?: string | null;
}

export function useUpdateMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMailboxInput) =>
      invokeAlwaysdata<{ ok: boolean; mailbox: unknown }>('alwaysdata-mailbox-upsert', {
        ...input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MAILBOXES_KEY }),
  });
}

export function useDeleteMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      invokeAlwaysdata<{ ok: boolean }>('alwaysdata-mailbox-delete', { id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MAILBOXES_KEY }),
  });
}
